import "./App.css";
import store, { persistor } from "./store";
import React, { useEffect, useState } from "react";

/* Pages */
import { Login } from "./pages/login/Login";
import { Drivers } from "./pages/drivers/Drivers";
import { Register } from "./pages/register/Register";
import { Vehicles } from "./pages/vehicles/Vehicles";
import { Addresses } from "./pages/addresses/Addresses";
import { Dashboard } from "./pages/dashboard/Dashboard";
import { Deliveries } from "./pages/deliveries/Deliveries";
import { AddVehicle } from "./pages/addVehicle/AddVehicle";
import { PackageList } from "./pages/packageList/PackageList";
import { Integrations } from "./pages/integrations/Integrations";
import { ResetPassword } from "./pages/resetPassword/ResetPassword";
import { ForgotPassword } from "./pages/forgotPassword/ForgotPassword";
import { ScheduledDeliveries } from "./pages/scheduledDeliveries/ScheduledDeliveries";

/* Modals */
import { Toaster } from "react-hot-toast";
import { ErrorMsg } from "./components/errorMsg/ErrorMsg";
import { AddLaundry } from "./components/modals/AddLaundry";
import { DriverInfo } from "./components/modals/DriverInfo";
import { DeleteModal } from "./components/modals/DeleteModal";
import { GenerateKey } from "./components/modals/generateKey/GenerateKey";
import { AddNewDriver } from "./components/modals/AddNewDriver";
import { ApiKeyModal } from "./components/modals/apiKeyModal/ApiKeyModal";
import { EditDelivery } from "./components/modals/EditDelivery";
import { DeleteVehicle } from "./components/modals/deleteVehicle/DeleteVehicle";
import { AllDriversMap } from "./components/modals/allDriversMap/AllDriversMap";
import { ThreeDotsModal } from "./components/modals/ThreeDotsModal";
import { DriverLocation } from "./components/modals/DriverLocation";
import { OrderInformation } from "./components/UI/OrderInformation";
import { AddCustomerAddress } from "./components/modals/AddCustomerAddress";
import { DeleteCustomerAddress } from "./components/modals/DeleteCustomerAddress";
import { SuccessNotificationModal } from "./components/modals/SuccessNotification";

/* Components */
import { Header } from "./components/header/Header";
import { Sidebar } from "./components/sidebar/Sidebar";

/* Functions */
import { fetchDriverLocation } from "./redux/driver/thunks/getDriverLocation";

import { Provider } from "react-redux";
import { useDispatch, useSelector } from "react-redux";
import { Route, Routes, useLocation, Navigate } from "react-router-dom";
import {
  closeAllModals,
  setErrorMsg,
  setIsErrorMsg,
  setIsModalsBgShown,
  toggleAddresses,
  toggleDashboard,
  toggleDeliveries,
  toggleDrivers,
} from "./redux/globalFunctions/globalFnSlice";
import { FadeOnLanguageChange } from "./components/FadeOutOnLanguageChange";

function App() {
  const {
    showThreeDotsModal,
    isModalsBgShown,
    isDeleteModalShown,
    isDeleteConfirmShown,
    isEditModalShown,
    isAddNewDriverShown,
    isDriverLocationShown,
    isDriverInfoShown,
    isAddNewCustomerAddress,
    isDeleteCustomerAddress,
    isAddLaundry,
    isErrorMsg,
    errorMsg,
    successMsg,
    isMapShown,
    isGenerateApiShown,
    isApiKeyModalShown,
    isOrderInformationOpen,
    isDeleteVehicleShown,
  } = useSelector((state) => state.global);

  const location = useLocation();
  const dispatch = useDispatch();
  const { token, tokenExpiryTime } = useSelector((state) => state.user);
  const { deliveriesToDisplay, language } = useSelector(
    (state) => state.global
  );
  const { deliveries } = useSelector((state) => state.deliveries);
  const { deliveries: deliveriesByDate } = useSelector(
    (state) => state.deliveriesByDate
  );

  const [isPathChecked, setIsPathChecked] = useState(false);

  const [showOrderInformation, setShowOrderInformation] = useState(false);

  useEffect(() => {
    setTimeout(() => {
      setShowOrderInformation(true);
    }, 300);
  }, []);

  useEffect(() => {
    if (token !== null) {
      dispatch(fetchDriverLocation({}));
    }
  }, [token]);

  const sessionExpiredText =
    language === "en"
      ? "Your session has expired. Redictering to login page."
      : "Økten din har utløpt. Omdirigerer til innloggingssiden.";

  // Function for logging out the user if the token expires.
  useEffect(() => {
    if (!tokenExpiryTime) return;

    const currentTime = new Date().getTime();
    if (tokenExpiryTime) {
      const timeLeft = tokenExpiryTime - currentTime;
      setTimeout(() => {
        dispatch(setIsModalsBgShown(true));
        dispatch(setIsErrorMsg(true));
        dispatch(setErrorMsg(sessionExpiredText));
        setTimeout(() => {
          dispatch({ type: "RESET_STORE" });
          persistor.purge();
        }, 2000);
      }, timeLeft);
    }
  }, [tokenExpiryTime, dispatch, location.pathname]);

  const [isFadingOut, setIsFadingOut] = useState(false);

  useEffect(() => {
    if (location.pathname === "/deliveries") {
      dispatch(toggleDeliveries());
    } else if (location.pathname === "/dashboard") {
      dispatch(toggleDashboard());
    } else if (location.pathname === "/drivers") {
      dispatch(toggleDrivers());
    } else if (location.pathname === "/addresses") {
      dispatch(toggleAddresses());
    }
    setIsPathChecked(true);
  }, [location.pathname]);

  if (!isPathChecked) {
    // Return null or a loader while the path is being checked
    return null;
  }

  return (
    <Provider store={store}>
      <FadeOnLanguageChange>
        <Toaster position="top-center" reverseOrder={true} />
        {token && (
          <div className="content">
            <Sidebar />
            <div
              className="headerAndContent"
              style={{
                width:
                  (location.pathname === "/dashboard" &&
                    deliveries.length !== 0 &&
                    showOrderInformation) ||
                  (location.pathname === "/dashboard" &&
                    showOrderInformation &&
                    deliveriesByDate.length !== 0)
                    ? "55%"
                    : "calc(100vw - 200px)",
              }}
            >
              {location.pathname !== "/add-vehicle" &&
                !location.pathname.includes("/edit-vehicle") && <Header />}

              <Routes>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/deliveries" element={<Deliveries />} />
                <Route
                  path="/scheduled-deliveries"
                  element={<ScheduledDeliveries />}
                />
                <Route path="/drivers" element={<Drivers />} />
                <Route path="/addresses" element={<Addresses />} />
                <Route path="/integrations" element={<Integrations />} />
                <Route path="/vehicles" element={<Vehicles />} />
                <Route path="/add-vehicle" element={<AddVehicle />} />
                <Route path="/edit-vehicle/:id" element={<AddVehicle />} />
                <Route path="/package-list/:id" element={<PackageList />} />
                <Route
                  path="*"
                  element={<Navigate to={"/dashboard"} replace />}
                />
              </Routes>
            </div>
            {location.pathname === "/dashboard" &&
              deliveriesToDisplay &&
              deliveriesToDisplay?.length !== 0 &&
              showOrderInformation && <OrderInformation />}
          </div>
        )}

        {!token && (
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/reset" element={<ResetPassword />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="*" element={<Navigate to={"/login"} replace />} />
          </Routes>
        )}

        {showThreeDotsModal && <ThreeDotsModal />}
        {isDeleteModalShown && <DeleteModal />}

        {isEditModalShown && <EditDelivery />}
        {isAddNewDriverShown && <AddNewDriver />}
        {isDriverInfoShown && <DriverInfo />}
        {isAddNewCustomerAddress && <AddCustomerAddress />}
        {isDeleteCustomerAddress && <DeleteCustomerAddress />}
        {isAddLaundry && <AddLaundry />}
        {isMapShown && <AllDriversMap />}
        {isGenerateApiShown && <GenerateKey />}
        {isApiKeyModalShown && <ApiKeyModal />}
        {isDriverLocationShown && <DriverLocation />}
        {isDeleteVehicleShown && <DeleteVehicle />}
        {isModalsBgShown &&
          (isOrderInformationOpen || isDeleteConfirmShown || isErrorMsg) && (
            <div
              className="modals"
              onClick={() => {
                if (
                  window.location.pathname === "/deliveries" &&
                  isOrderInformationOpen
                ) {
                  dispatch(closeAllModals());
                }
              }}
              style={{
                justifyContent: isOrderInformationOpen ? "flex-end" : "",
              }}
            >
              <div
                className="modal-content"
                onClick={(e) => e.stopPropagation()}
              >
                {isOrderInformationOpen && <OrderInformation />}
                {isErrorMsg && <ErrorMsg msg={errorMsg} />}
                {isDeleteConfirmShown && (
                  <SuccessNotificationModal msg={successMsg} />
                )}
              </div>
            </div>
          )}
      </FadeOnLanguageChange>
    </Provider>
  );
}

export default App;
